import { EntityStoreName } from 'app/enums/store';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { modalsSelectorFactory } from 'app/store/modal/selectors';
import { modalsReducers } from 'app/store/modal/slice';
import { dispatchUnwrap } from 'app/utils/store';
import config from 'config/config';
import { CreateFeedbackForm } from 'modules/feedback/components/CreateFeedbackForm';
import { FeedbackStoreStatus } from 'modules/feedback/store/enums';
import { createFeedback } from 'modules/feedback/store/repository';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { ModalName } from 'app/enums';
import { Box, Typography, Button } from '@mui/material';
import { useCreateFeedbackForms } from 'modules/feedback/hooks/use-create-feedback-form';
import { useDispatch, useSelector } from 'react-redux';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { Modal } from 'shared/components/Modal';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { useStyles } from './FeedbackModal.styles';
import appLogo from 'assets/images/okletsplay_logo-text.png';

export const FeedbackModalComponent: FC = () => {
  const dispatch = useDispatch();
  const message: string = useSelector(modalsSelectorFactory.useModalPayload(ModalName.Feedback));
  const { classes } = useStyles();
  const { form } = useCreateFeedbackForms(message);
  const {
    handleSubmit,
    formState: { isValid, isSubmitted },
  } = form;
  const [isCreated, setIsCreated] = useState(false);

  const title = useMemo(
    () => (isCreated ? 'Thank You!' : 'Tell Us How We Are Doing.'),
    [isCreated]
  );

  const subTitle = useMemo(
    () =>
      isCreated
        ? 'Your feedback has been successfully sent to the OkLetsPlay team.'
        : 'Let us know how we can keep improving OkLetsPlay and making your favorite games even fun to play.',
    [isCreated]
  );

  const isCreating = useEntityRepositoryStatus({
    [EntityStoreName.Feedbacks]: FeedbackStoreStatus.Creating,
  });

  const contentHeight = useMemo(() => {
    if (isCreated) {
      return 130;
    }

    return !isValid && isSubmitted ? 300 : 272;
  }, [isValid, isSubmitted, isCreated]);

  const onSubmitHandler = useCallback((payload) => {
    dispatchUnwrap(createFeedback(payload)).then(() => setIsCreated(true));
  }, []);

  const onCloseHandler = useCallback(
    () => dispatch(modalsReducers.closeModal(ModalName.Feedback)),
    [dispatch]
  );

  const onJoinToDiscordHandler = useCallback(() => {
    dispatch(modalsReducers.closeModal(ModalName.Feedback));
    window.open(config.discordInviteUrl, '_blank', 'noreferrer');
  }, [dispatch]);

  return (
    <Modal
      maxWidth="xs"
      title="Feedback"
      name={ModalName.Feedback}
      isHeaderHighlighted={false}
      isFooterHighlighted={false}
      contentHeight={contentHeight}
      header={
        <Box className={classes.header}>
          <img src={appLogo} alt="logo" />
        </Box>
      }
      actions={
        <>
          {!isCreated ? (
            <LoaderButton
              fullWidth
              color="primary"
              variant="contained"
              loading={isCreating}
              onClick={handleSubmit(onSubmitHandler)}
            >
              Send
            </LoaderButton>
          ) : (
            <Box className={classes.actionsContainer}>
              <Button color="primary" variant="outlined" onClick={onCloseHandler}>
                Return to lobby
              </Button>
              <Button color="primary" variant="contained" onClick={onJoinToDiscordHandler}>
                Join to Discord
              </Button>
            </Box>
          )}
        </>
      }
    >
      <Box className={classes.root}>
        <Typography className={classes.title} variant="h4">
          {title}
        </Typography>
        <Typography className={classes.subtitle} variant="body1">
          {subTitle}
        </Typography>
        {!isCreated && <CreateFeedbackForm form={form} />}
      </Box>
    </Modal>
  );
};

export const FeedbackModal = withModalOpenChecker(FeedbackModalComponent, ModalName.Feedback);
